import React, { FC, useCallback, useEffect, useState } from 'react';

import { DownloadOutlined } from '@ant-design/icons';
import { Button, Select } from 'antd';
// import DownloadSwitcher from 'component/download-switcher';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { Label, Modal, Table, Typography } from 'ui';
import BackLink from 'ui/BackLink';

import {
  useLazyExportAttendeesQuery,
  useLazyGetEventAttendeesQuery,
  useLazyGetEventDetailQuery,
} from 'services/events/eventsApiService';

import Pagination from '../../../component/pagination';
import SectionTitle from '../../../component/section-title/sectionTitle';
import s from './EventAttendeesList.module.scss';

const initialFilter = {
  PageNumber: 1,
  PageSize: 25,
};
const EventAttendeesList: FC = () => {
  const { eventId } = useParams();
  const [exportAttendees] = useLazyExportAttendeesQuery();
  const [requestFilter, setRequestFilter] = useState(initialFilter);
  const [getAttendees, { isFetching, data: attendees }] = useLazyGetEventAttendeesQuery();
  const [isDownLoading, setIsDownLoading] = useState<boolean>();
  // const [isPDF, setIsPDF] = useState<boolean>(false);
  const [ticketType, setTicketType] = useState<string>('');
  const [getEventDetail, { data: eventDetail }] = useLazyGetEventDetailQuery();

  const [open, setOpen] = useState(false);
  const showModal = () => {
    setOpen(true);
  };
  const handleOk = () => {
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleDownloadAttendees = useCallback(() => {
    setIsDownLoading(true);
    if (eventId) {
      exportAttendees({ EventId: eventId, TicketTypeId: ticketType }).then((data) => {
        if (data.data) {
          const link = document.createElement('a');
          link.href = data.data?.fileName;
          link.setAttribute('download', `attendees-${eventId}.csv`);
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setIsDownLoading(false);
        }
      });
    }
  }, [exportAttendees, ticketType, eventId]);

  const columns = [
    {
      title: 'Event date',
      dataIndex: 'show',
      key: 'show',
      render: (show: { date: string; time: string }) => {
        return (
          <div>
            {dayjs(show.date).format('DD.MM.YYYY')} {dayjs(show.time).format('HH:mm')}
          </div>
        );
      },
    },
    {
      title: 'Receipt No',
      dataIndex: 'receiptNumber',
      key: 'receiptNumber',
    },
    {
      title: 'Order No',
      dataIndex: 'order',
      key: 'order',
      render: (order: { orderNo: string }) => <div>{order.orderNo}</div>,
    },
    {
      title: 'Order Date',
      dataIndex: 'order',
      key: 'orderDate',
      render: (order: { orderDate: string }) => (
        <div>{dayjs(order.orderDate).format('DD.MM.YYYY HH:mm')}</div>
      ),
    },
    {
      title: 'Payment Type',
      dataIndex: 'order',
      key: 'paymentType',
      render: (order: { paymentType: string }) => <div>{order.paymentType}</div>,
    },
    {
      title: 'Ordered By',
      dataIndex: 'order',
      key: 'orderedBy',
      render: (order: { user: { fullName: string } }) => <div>{order.user.fullName}</div>,
    },
    {
      title: ' User Email',
      dataIndex: 'order',
      key: 'orderUserEmail',
      render: (order: { user: { email: string } }) => <div>{order.user.email}</div>,
    },
    {
      title: 'Name on Ticket',
      dataIndex: 'ticketHolderPersonalInfo',
      key: 'ticketHolderPersonalInfo',
      render: (holderInfo: { firstName: string; lastName: string }) => (
        <div>{`${holderInfo.firstName} ${holderInfo.lastName}`}</div>
      ),
    },
    {
      title: 'Mobile No',
      dataIndex: 'order',
      key: 'userMobile',
      render: (order: { user: { mobileNumber: string } }) => <div>{order.user.mobileNumber}</div>,
    },
    {
      title: 'Ticket Type',
      dataIndex: 'ticketType',
      key: 'ticketType',
      render: (ticketType: { name: string }) => <div>{ticketType.name}</div>,
    },
    {
      title: 'Qty',
      dataIndex: 'numberOfPersons',
      key: 'numberOfPersons',
    },
    {
      title: 'Amount',
      dataIndex: 'subTotal',
      key: 'amount',
      render: (total: number) => <div>{total} €</div>,
    },
    {
      title: 'Barcode Number',
      dataIndex: 'barCode',
      key: 'orderBarcode',
    },
  ];

  const showedElements =
    Number(attendees?.attendees?.length) +
    (Number(attendees?.pageNumber) - 1) * Number(requestFilter.PageSize);

  useEffect(() => {
    if (eventId) {
      getAttendees({ id: eventId, ...requestFilter });
    }
  }, [eventId, getAttendees, requestFilter]);

  useEffect(() => {
    if (eventId) {
      getEventDetail(eventId);
    }
  }, [getEventDetail, eventId]);

  return (
    <div className={s.wrapper}>
      <BackLink />
      <div className={s.header}>
        <div className={s.block}>
          <div>
            <SectionTitle
              content={
                eventDetail?.basicInfo?.eventName
                  ? `${eventDetail.basicInfo.eventName}: Attendees`
                  : 'Attendees'
              }
              className={s.title}
              withOutMargin
            />
          </div>

          <Button
            icon={<DownloadOutlined />}
            size="large"
            loading={isDownLoading}
            style={{ fontSize: 13 }}
            type="primary"
            onClick={showModal}>
            EXPORT
          </Button>
        </div>

        {/* <Breadcrumbs routes={BREADCRUMBS_ROUTES} className={s.breadcrumbs} /> */}
      </div>

      <Modal
        open={open}
        onOk={handleOk}
        handleClose={handleCancel}
        footer={
          <div
            style={{
              marginLeft: 'auto',
              marginRight: 'auto',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Button
                icon={<DownloadOutlined />}
                loading={isDownLoading}
                type="primary"
                size="large"
                onClick={handleDownloadAttendees}
                style={{ fontSize: 13 }}>
                DOWNLOAD
              </Button>
              <Button style={{ fontSize: 13 }} size="large" onClick={handleCancel}>
                CANCEL
              </Button>
            </div>
            <Label>Please disable all popup blockers</Label>
          </div>
        }>
        <Modal.Header>
          <Typography type="h3" weight={700}>
            Choose file type for Attendee list
          </Typography>
          {/* <div style={{ marginTop: 16, marginBottom: 16 }}>
            <DownloadSwitcher value={isPDF} onChange={setIsPDF} />
          </div> */}
        </Modal.Header>
        <div style={{ marginTop: 24 }}></div>
        {/* {isPDF ? (
          <Typography type="h6">
            PDF is a nicely formatted document showing the name on the ticket and who ordered it.
          </Typography>
        ) : ( */}
        <Typography type="h6">
          CSV — Transaction list which includes all important fields including, if available, user
          address, phone numbers, and other information requested by event in comma delimited
          format.
        </Typography>
        {/* )} */}
        <div style={{ marginTop: 16, marginBottom: 24 }}>
          <Label>Ticket type (if you wish to show only specific ticket types)</Label>
          <Select
            onChange={(value) => setTicketType(value)}
            style={{ width: '100%', height: 38 }}
            options={[
              { label: 'All', value: '' },
              ...(eventDetail?.ticketTypes?.map((item) => ({
                label: item.name,
                value: item.id,
              })) || []),
            ]}
          />
        </div>
      </Modal>

      <Table
        columns={columns}
        loading={isFetching}
        dataSource={attendees?.attendees}
        scroll={{ x: '100%' }}
        pagination={false}
      />
      <Pagination
        handlePageChange={(page) =>
          setRequestFilter((prevValue) => ({ ...prevValue, PageNumber: page }))
        }
        showedElements={showedElements}
        totalElements={attendees?.totalRecords}
        total={attendees?.totalPages}
        currentPage={attendees?.pageNumber || 1}
        handleSizeChange={(size) =>
          setRequestFilter((prevValue) => ({ ...prevValue, PageSize: size }))
        }
        defaultPageSize={25}
      />
    </div>
  );
};

export default EventAttendeesList;
